import type {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'
import type { App } from 'vue'
import { auth0 } from './auth0'
import { useLocale } from '@/modules/Core/composables'
import {
  REFRESH_TOKEN_ENDPOINT,
  UNAUTHORIZED_STATUS_CODE,
} from '@/modules/Core/constants'
import i18n from '@/modules/Core/plugins/i18n'
import useToastStore from '@/modules/Core/store/toast'
import useAuthStore from '@/modules/Auth/store'
import {
  CARS_SEARCH_DIRECT_ENDPOINT,
  FLIGHTS_SEARCH_DIRECT_ENDPOINT,
  HOTELS_SEARCH_DIRECT_ENDPOINT,
} from '@/modules/Search/requests/mutations'
import featureFlag from '@/config/featureFlag'
import type { RefreshTokenSuccessResponse } from '@/modules/Auth/types/api/response/refreshToken'

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Customer-Code': featureFlag.customerCode,
  },
})

function onRequest(config: InternalAxiosRequestConfig) {
  const { currentLocale } = useLocale()
  config.headers['X-Language'] = currentLocale.value

  const authStore = useAuthStore()
  const toastStore = useToastStore()
  const t = i18n.global.t

  const token =
    featureFlag.authMode === 'legacy'
      ? authStore.tokens.access
      : localStorage.getItem('id_token')

  if (authStore.loggedIn && !token) {
    authStore.logout()

    const message = t('yourSessionHasBeenExpiredPleaseLoginAgain')

    toastStore.addToast({
      color: 'danger',
      title: message,
    })

    return Promise.reject(new Error(message))
  }

  const SEARCH_ENDPOINTS = [
    FLIGHTS_SEARCH_DIRECT_ENDPOINT,
    HOTELS_SEARCH_DIRECT_ENDPOINT,
    CARS_SEARCH_DIRECT_ENDPOINT,
  ]

  // DISABLE AUTH FOR SEARCH
  if (config.url && !SEARCH_ENDPOINTS.includes(config.url)) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
}

function onResponse(config: AxiosResponse) {
  return config
}

async function onResponseError(error: AxiosError<ServerResponse>) {
  if (!error.response) {
    throw error
  }

  const originalConfig = error.config as ExtendedAxiosRequestConfig

  if (!originalConfig) {
    throw error
  }

  const authStore = useAuthStore()
  const toastStore = useToastStore()
  const t = i18n.global.t

  // LEGACY
  if (
    featureFlag.authMode === 'legacy' &&
    authStore.loggedIn &&
    originalConfig.url === REFRESH_TOKEN_ENDPOINT
  ) {
    authStore.logout()

    toastStore.addToast({
      color: 'danger',
      title: t('yourSessionHasBeenExpiredPleaseLoginAgain'),
    })

    return
  }

  if (
    featureFlag.authMode === 'legacy' &&
    authStore.loggedIn &&
    error.response.status === UNAUTHORIZED_STATUS_CODE
  ) {
    const { data } = await axiosInstance.post<RefreshTokenSuccessResponse>(
      REFRESH_TOKEN_ENDPOINT,
      {
        refreshToken: authStore.tokens.refresh,
      }
    )

    authStore.setTokens(data)

    await axiosInstance.request(originalConfig)
  }

  // AUTH0
  if (
    featureFlag.authMode === 'auth0' &&
    authStore.loggedIn &&
    error.response.status === UNAUTHORIZED_STATUS_CODE &&
    !originalConfig._retry
  ) {
    try {
      await auth0.getAccessTokenSilently({
        cacheMode: 'off',
      })
      const newToken = auth0.idTokenClaims.value?.__raw

      if (!newToken) {
        throw new Error('No new token found')
      }

      // Update the token in the store
      localStorage.setItem('id_token', newToken)

      // Mark the request as retried to prevent infinite loops
      originalConfig._retry = true
      originalConfig.headers.Authorization = `Bearer ${newToken}`
    } catch (refreshError) {
      // If token refresh fails, logout the user immediately
      authStore.logout()
      toastStore.addToast({
        color: 'danger',
        title: t('yourSessionHasBeenExpiredPleaseLoginAgain'),
      })
      auth0.logout()
      localStorage.removeItem('id_token')
      return
    }
  }

  throw error
}

axiosInstance.interceptors.request.use(onRequest)
axiosInstance.interceptors.response.use(onResponse, onResponseError)

function registerAxios(app: App) {
  app.config.globalProperties.$axios = { ...axiosInstance }
}

export { axiosInstance, registerAxios }
